import "./pickup-delivery-details.css";

import { Button } from "@tql/ui-components-react/tql/buttons";
import { bookOpen_01 } from "@tql/ui-components-react/untitled/icons";

import {
	convertSummaryToUpdateBody,
	useAddressBook,
} from "~/features/address-book/hooks/use-address-book.ts";
import { useApplyAddressBookLocation } from "~/features/quote/hooks/use-apply-address-book-location.ts";
import { isPristineSavedAddress } from "~/features/quote/utils/applied-address.ts";
import { buildAddressBookLocationFilter } from "~/features/quote/utils/build-address-book-location-filter.ts";
import { AddressBookModal } from "~/shared/components/address-book-modal/address-book-modal.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { useAutoSpinner } from "~/shared/hooks/use-global-spinner.ts";
import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";
import type { SubGroupKey } from "~/shared/types/quote-details-types.ts";
import { capitalizeWord } from "~/shared/utils/string-fns.ts";

import { LocationTypeOptions } from "./components/location-type-options/location-type-options.tsx";
import { PostalCodeSearchRow } from "./components/postal-code-search-row/postal-code-search-row.tsx";
import { ServiceOptions } from "./components/service-options/service-options.tsx";
import { useAddressPostalCodeSelection } from "./hooks/use-address-postal-code-selection.ts";
import { usePickupDeliveryDetails } from "./hooks/use-pickup-delivery-details.ts";

type PickupDeliveryDetailsProps = {
	name: SubGroupKey;
	showValidationError?: boolean;
	onClearValidation?: () => void;
};

export const PickupDeliveryDetails = ({
	name,
	showValidationError = false,
	onClearValidation,
}: PickupDeliveryDetailsProps) => {
	const pickupDeliveryDetails = usePickupDeliveryDetails(name);
	const {
		handleCombinedSelectionChange,
		handleRadioSelectionChange,
		isOptionSelected,
		quoteState,
	} = pickupDeliveryDetails;
	const {
		addressBookItem,
		applyAddressBookLocation,
		applyAddressBookSelection,
		clearInvalidCountryAddress,
		handleAddressBookWithPrompt,
		handleAddressPostalCodeChange,
		invalidCountryAddress,
		onToggleAddressBookModal,
		pendingAddressBookItem,
		searchValue,
		selectedAddressBookItems,
		setPendingAddressBookItem,
		setSelectedAddressBookItem,
		showAddressBookModal,
		stopType,
	} = useAddressPostalCodeSelection({ name, ...pickupDeliveryDetails });
	const { updateAddressBookItem, isUpdating } = useAddressBook();
	const { trackUse } = useApplyAddressBookLocation(
		name,
		pickupDeliveryDetails.countries,
	);

	useAutoSpinner(isUpdating);

	const isMobile = useIsMobile();
	const addressBookDisabled =
		!quoteState.customer || quoteState.customer.id == 0;

	const saveAccessorials = async () => {
		if (!addressBookItem) return;
		const accessorials =
			name === "pickup"
				? quoteState.pickupServices
				: quoteState.deliveryServices;
		const accessorialsStr = JSON.stringify(accessorials.map((a) => a.id));

		const targetStopType = name === "pickup" ? 1 : 2;
		const hasTargetDetail = addressBookItem.details?.some(
			(d) => d.stopType === targetStopType,
		);

		const updatedDetails = hasTargetDetail
			? addressBookItem.details.map((d) => {
					return d.stopType === targetStopType
						? {
								...d,
								accessorials: accessorialsStr,
							}
						: d;
				})
			: [
					...(addressBookItem.details || []),
					{
						addressBookItemId: addressBookItem.addressBookItemId,
						rowVersion: null,
						contactDetailId: 0,
						contactName: "",
						contactPhone: "",
						contactExtension: "",
						hoursOpen: "",
						hoursClose: "",
						stopType: targetStopType,
						mode: 0,
						timeZone: "",
						specialInstructions: null,
						accessorials: accessorialsStr,
					},
				];

		const updatedSummary = {
			...addressBookItem,
			details: updatedDetails,
		};

		const updateBody = convertSummaryToUpdateBody(updatedSummary);
		await updateAddressBookItem(updateBody);
		setSelectedAddressBookItem(name, updatedSummary);

		// If the other side has the same address book item ID, update it too to keep them in sync
		const otherName = name === "pickup" ? "delivery" : "pickup";
		if (
			selectedAddressBookItems[otherName]?.addressBookItemId ===
			updatedSummary.addressBookItemId
		) {
			setSelectedAddressBookItem(otherName, updatedSummary);
		}
	};

	return (
		<>
			<div className={"pickup-delivery-header-row"}>
				<h4>{capitalizeWord(name)}</h4>
				{isMobile ? (
					<Button
						disabled={addressBookDisabled}
						onClick={onToggleAddressBookModal}
						svgIcon={bookOpen_01}
						tabIndex={-1}
						variant={"tertiary"}
					>
						Address Book
					</Button>
				) : null}
			</div>

			{isMobile ? (
				<div className={"pickup-delivery-address-label"}>
					{capitalizeWord(name)} Address<span className={"required"}>*</span>
				</div>
			) : null}

			<PostalCodeSearchRow
				addressBookDisabled={addressBookDisabled}
				customerId={quoteState.customer?.id}
				disabled={!quoteState.customer?.id}
				hideAddressBookButton={isMobile}
				onAddressBookClick={onToggleAddressBookModal}
				onSelectionChange={(selectedItem) => {
					onClearValidation?.();
					handleAddressPostalCodeChange(selectedItem);
				}}
				showValidationError={showValidationError}
				stopType={stopType}
				validationMessage={`${capitalizeWord(name)} location is required`}
				value={searchValue}
			/>

			<LocationTypeOptions
				handleRadioSelectionChange={handleRadioSelectionChange}
				isOptionSelected={isOptionSelected}
				name={name}
			/>

			<ServiceOptions
				handleCombinedSelectionChange={handleCombinedSelectionChange}
				isOptionSelected={isOptionSelected}
				name={name}
			/>

			<div className={"save-accessorials-button"}>
				{isPristineSavedAddress(addressBookItem) &&
				!addressBookItem?.isLegacy ? (
					<Button
						disabled={
							!isPristineSavedAddress(addressBookItem) ||
							addressBookItem?.isLegacy
						}
						onClick={() => {
							void saveAccessorials();
						}}
						tabIndex={-1}
					>
						Save Accessorials
					</Button>
				) : null}
			</div>

			{showAddressBookModal ? (
				<AddressBookModal
					customerId={quoteState.customer?.id ?? 0}
					locationFilter={buildAddressBookLocationFilter(
						quoteState,
						name,
						selectedAddressBookItems[name],
					)}
					name={name}
					onClose={onToggleAddressBookModal}
					onSelect={(address) => {
						trackUse();
						handleAddressBookWithPrompt(address);
					}}
					useLocationFilter={true}
				/>
			) : null}

			{pendingAddressBookItem ? (
				<TraxModal
					cancelText={"Keep Current"}
					confirmText={"Use Address Book"}
					onCancel={() => {
						// Keep current accessorials, but still apply address/location
						applyAddressBookLocation(pendingAddressBookItem);
						setPendingAddressBookItem(null);
					}}
					onClose={() => {
						applyAddressBookSelection(pendingAddressBookItem);
						setPendingAddressBookItem(null);
					}}
					onConfirm={() => {
						applyAddressBookSelection(pendingAddressBookItem);
						setPendingAddressBookItem(null);
					}}
					title={"Overwrite Accessorials?"}
					type={"confirmation"}
					width={370}
				>
					<p>
						The selected address book entry has saved accessorials. Would you
						like to overwrite your current selections with the address book
						values?
					</p>
				</TraxModal>
			) : null}

			{invalidCountryAddress ? (
				<TraxModal
					cancelText={"Close"}
					onCancel={clearInvalidCountryAddress}
					onClose={clearInvalidCountryAddress}
					showConfirmButton={false}
					showConfirmationButtons={true}
					title={"Invalid Country Code"}
					type={"error"}
				>
					<p>
						This address book item has a country code of &quot;
						{invalidCountryAddress.country}&quot; that cannot be recognized as a
						valid country. Please contact TQL Tech Support at{" "}
						<strong>(800) 580-3101 ext. 56400</strong> to have this record
						corrected.
					</p>
				</TraxModal>
			) : null}
		</>
	);
};
